import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "./pd-trash-item.js";
import { LitElement, css, html, nothing } from "lit";
import { property, state } from "lit/decorators.js";
import { localized, msg, str } from "@lit/localize";
import "@progressive-development/pd-forms/pd-button";
import { pdIcons } from "@progressive-development/pd-icon";
import { repeat } from "lit/directives/repeat.js";
//#region src/pd-trash/PdTrashList.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
var PdTrashList = class PdTrashList extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.entries = [];
		this.loading = false;
		this.withRestore = true;
		this.withDelete = false;
		this._markedIds = /* @__PURE__ */ new Set();
	}
	static {
		this.styles = css`
    :host {
      display: block;
      container-type: inline-size;
    }

    /* Toolbar: marking group + counter left, actions right */
    .toolbar {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: var(--pd-spacing-sm, 8px);
      min-height: 2rem;
      padding-bottom: 0.25rem;
    }

    .select-all {
      display: flex;
      align-items: center;
      gap: 6px;
      cursor: pointer;
      font-size: 0.85rem;
      color: var(--pd-default-font-col, inherit);
    }

    .count {
      font-size: 0.8rem;
      color: var(--pd-default-font-muted-col, #999);
    }

    .toolbar-actions {
      margin-left: auto;
      display: flex;
      align-items: center;
      gap: var(--pd-spacing-sm, 8px);
    }

    /* Destructive action: danger coloring + extra distance to the
       constructive restore anchor right beside it — never confusable.
       The outline variant of pd-button reads the outline tokens. */
    .delete-marked {
      --pd-button-outline-border-col: var(--pd-default-error-col, #dc2626);
      --pd-button-outline-font-col: var(--pd-default-error-col, #dc2626);
      --pd-button-outline-bg-col-hover: var(
        --pd-default-error-light-col,
        #fef2f2
      );
      margin-right: var(--pd-spacing-md, 16px);
    }

    ul {
      list-style: none;
      margin: 0;
      padding: 0;
    }

    li {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      padding: var(--pd-trash-row-gap, 0) 0;
    }

    li + li {
      border-top: 1px solid
        var(--pd-trash-separator-col, var(--pd-default-light-col, #e0e0e0));
    }

    .row-check {
      margin-top: 0.85rem;
    }

    li pd-trash-item {
      flex: 1;
      min-width: 0;
    }

    .empty {
      color: var(--pd-default-font-muted-col, #757575);
      font-style: italic;
      padding: 0.6rem 0;
    }

    .skeleton {
      height: 3rem;
      margin: 0.6rem 0;
      border-radius: 4px;
      background: var(--pd-default-bg-light-col, #eee);
      animation: pulse 1.5s ease-in-out infinite;
    }

    @keyframes pulse {
      50% {
        opacity: 0.5;
      }
    }
  `;
	}
	willUpdate(changedProps) {
		if (changedProps.has("entries") && this._markedIds.size) {
			const ids = new Set(this.entries.map((entry) => entry.id));
			this._markedIds = new Set([...this._markedIds].filter((id) => ids.has(id)));
		}
	}
	render() {
		return html`
      <div class="head"><slot name="head"></slot></div>
      ${this.loading ? this._renderLoading() : this._renderContent()}
      <div class="footer"><slot name="footer"></slot></div>
    `;
	}
	/**
	* Filter hook for the upcoming search/type-filter extension —
	* the render list always runs through this step.
	*/
	get _visibleEntries() {
		return this.entries;
	}
	_renderLoading() {
		return html`
      <div
        role="status"
        aria-label="${msg("Papierkorb wird geladen", { id: "pd.trash.list.loading" })}"
      >
        <div class="skeleton"></div>
        <div class="skeleton"></div>
        <div class="skeleton"></div>
      </div>
    `;
	}
	get _withActions() {
		return this.withRestore || this.withDelete;
	}
	_renderContent() {
		const entries = this._visibleEntries;
		if (entries.length === 0) return html`
        <div class="empty">
          ${this.emptyText ?? msg("Der Papierkorb ist leer", { id: "pd.trash.list.empty" })}
        </div>
      `;
		return html`
      ${this._withActions ? this._renderToolbar(entries) : nothing}
      <ul role="list">
        ${repeat(entries, (entry) => entry.id, (entry) => html`
            <li>
              ${this._withActions ? html`
                      <input
                        type="checkbox"
                        class="row-check"
                        .checked="${this._markedIds.has(entry.id)}"
                        aria-label="${msg(str`${this.titleResolver?.(entry) ?? entry.originalId} markieren`, { id: "pd.trash.row.mark" })}"
                        @change="${(e) => this._toggleMark(entry.id, e.target.checked)}"
                      />
                    ` : nothing}
              <pd-trash-item
                .entry="${entry}"
                .titleResolver="${this.titleResolver}"
                .typeLabels="${this.typeLabels}"
                .fieldLabels="${this.fieldLabels}"
                .valueFormatter="${this.valueFormatter}"
              ></pd-trash-item>
            </li>
          `)}
      </ul>
    `;
	}
	_renderToolbar(entries) {
		const marked = this._markedIds.size;
		const allMarked = marked > 0 && marked === entries.length;
		return html`
      <div class="toolbar">
        <label class="select-all">
          <input
            type="checkbox"
            .checked="${allMarked}"
            .indeterminate="${marked > 0 && !allMarked}"
            @change="${(e) => this._toggleAll(entries, e.target.checked)}"
          />
          <span>${msg("Alle markieren", { id: "pd.trash.select-all" })}</span>
        </label>
        ${marked > 0 ? html`
                <span class="count">
                  ${msg(str`${marked} markiert`, { id: "pd.trash.marked-count" })}
                </span>
                <div class="toolbar-actions">
                  ${this.withDelete ? html`
                          <pd-button
                            class="delete-marked"
                            outline
                            size="sm"
                            icon="${pdIcons.ICON_DELETE_NEW}"
                            text="${msg(str`Endgültig löschen (${marked})`, { id: "pd.trash.delete" })}"
                            @button-clicked="${this._emitDelete}"
                          ></pd-button>
                        ` : nothing}
                  ${this.withRestore ? html`
                          <pd-button
                            outline
                            size="sm"
                            icon="${pdIcons.ICON_SYNC}"
                            text="${msg(str`Wiederherstellen (${marked})`, { id: "pd.trash.restore" })}"
                            @button-clicked="${this._emitRestore}"
                          ></pd-button>
                        ` : nothing}
                </div>
              ` : nothing}
      </div>
    `;
	}
	_toggleMark(id, checked) {
		const next = new Set(this._markedIds);
		if (checked) next.add(id);
		else next.delete(id);
		this._markedIds = next;
	}
	_toggleAll(entries, checked) {
		this._markedIds = checked ? new Set(entries.map((entry) => entry.id)) : /* @__PURE__ */ new Set();
	}
	_markedItems() {
		return this.entries.filter((entry) => this._markedIds.has(entry.id));
	}
	_emitRestore() {
		this.dispatchEvent(new CustomEvent("pd-trash-restore", {
			detail: { items: this._markedItems() },
			bubbles: true,
			composed: true
		}));
	}
	_emitDelete() {
		this.dispatchEvent(new CustomEvent("pd-trash-delete", {
			detail: { items: this._markedItems() },
			bubbles: true,
			composed: true
		}));
	}
};
__decorate([property({ attribute: false })], PdTrashList.prototype, "entries", void 0);
__decorate([property({ type: Boolean })], PdTrashList.prototype, "loading", void 0);
__decorate([property({ attribute: false })], PdTrashList.prototype, "titleResolver", void 0);
__decorate([property({ attribute: false })], PdTrashList.prototype, "typeLabels", void 0);
__decorate([property({ attribute: false })], PdTrashList.prototype, "fieldLabels", void 0);
__decorate([property({ attribute: false })], PdTrashList.prototype, "valueFormatter", void 0);
__decorate([property({ type: Boolean })], PdTrashList.prototype, "withRestore", void 0);
__decorate([property({ type: Boolean })], PdTrashList.prototype, "withDelete", void 0);
__decorate([property()], PdTrashList.prototype, "emptyText", void 0);
__decorate([state()], PdTrashList.prototype, "_markedIds", void 0);
PdTrashList = __decorate([localized()], PdTrashList);
//#endregion
export { PdTrashList };
